import BubbleAlert from '@go-client/components/BubbleUI/BubbleAlert';
import {
  BubbleFormActions,
  BubbleFormFooter,
  BubbleFormHeader,
  FormPageComponentHelpfulProps,
} from '@go-client/components/BubbleUI/BubbleForm';
import BubbleButton from '@go-client/components/BubbleUI/BubbleButton';
import BubbleOverlay from '@go-client/components/BubbleUI/BubbleOverlay';
import { FormGenericContainer } from '@go-client/components/FormGenericContainer';
import {
  EventTemplateFormikForm,
  EventTemplateFormikMainTab,
  EventTemplateFormikReservablesTab,
} from '@go-client/components/Formik/EventTemplateFormik';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { mapBy } from '@go-shared/lib/functional';
import { ReservableEventTemplateSnapshot } from '@go-shared/types/reservable';
import { FormikProps } from 'formik';
import React from 'react';
import { RESERVABLE_SCHEDULE_BREADCRUMBS } from '../ReservableSchedulePath';

interface SelectEventPagePropsBase {
  isBusy: boolean;
  setIsBusy: (isBusy: boolean) => void;
  libraryLocked: boolean;
  selectedEventSnapshot?: ReservableEventTemplateSnapshot;
  eventFormik: FormikProps<EventTemplateFormikForm>;
  onSaveOnly?: () => void;
}

type SelectEventPageProps = SelectEventPagePropsBase &
  FormPageComponentHelpfulProps<RESERVABLE_SCHEDULE_BREADCRUMBS>;

export default function SelectEventPage(props: SelectEventPageProps) {
  const { activeProperty } = useActiveProperty();
  const { brand } = useServerInfo();

  const forwardDisabled =
    props.eventFormik.values.title.trim() === '' ||
    !!props.eventFormik.errors.callToActions ||
    !!props.eventFormik.errors.reservableFields;

  return (
    <div className="eaf-1">
      <FormGenericContainer
        className="h-screen flex flex-col"
        bodyClassName="flex-grow flex flex-col"
        footer={() => (
          <>
            <BubbleFormFooter
              className="text-slate-dark mr-auto"
              items={[{ label: 'Experience Selected', value: props.eventFormik.values.title }]}
            />
            {props.onSaveOnly && (
              <BubbleOverlay loading={props.isBusy}>
                <BubbleButton variant="slate" inverse icon="floppy-disk" onClick={props.onSaveOnly}>
                  Save Details Only
                </BubbleButton>
              </BubbleOverlay>
            )}
            <BubbleFormActions
              isBusy={props.isBusy}
              forwardTitle={props.forwardTitle || 'Next: Enter Schedules'}
              backTitle={props.backTitle}
              onGoForward={props.onGoForward}
              onGoBack={props.onGoBack}
              forwardDisabled={forwardDisabled}
            />
          </>
        )}
      >
        <BubbleFormHeader<RESERVABLE_SCHEDULE_BREADCRUMBS>
          title={`${props.title || 'Create Experience'}`}
          subtitle={props.subtitle || 'Enter Details'}
          pageIndex={props.pageIndex ?? 1}
          onClose={props.onClose}
          activeBreadcrumb="event"
          breadcrumbs={props.breadcrumbs}
          onBreadcrumbClick={props.onBreadcrumbClick}
        />
        <div className="grid grid-cols-1 gap-4">
          {props.libraryLocked && (
            <BubbleAlert className="mb-3" variant="ocean">
              <span className="font-semibold">This template cannot be modified.</span> It may be a
              brand level event or you lack library permissions.
            </BubbleAlert>
          )}
          <EventTemplateFormikMainTab
            type="create"
            readOnly={props.libraryLocked}
            disabled={props.libraryLocked}
            formik={props.eventFormik}
            property={activeProperty}
            brand={brand}
            coreFieldsOnly
            segmentsMap={mapBy(brand.segments, s => s._id)}
            levelsMap={mapBy(brand.levels, l => l._id)}
            attributesMap={mapBy(brand.attributes, a => a._id)}
          />
          <EventTemplateFormikReservablesTab
            type="create"
            formik={props.eventFormik}
            readOnly={props.libraryLocked}
            disabled={props.libraryLocked}
          />
        </div>
      </FormGenericContainer>
    </div>
  );
}
